import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Typography, Tag, Avatar, Tooltip } from 'antd';
import { RobotOutlined, SendOutlined, CloseOutlined, AudioOutlined, UserOutlined, ThunderboltOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import useVoiceInput from '../utils/voice';
import AiPlanCard, { downloadAiFile } from './AiPlanCard';

const { Text } = Typography;

// Quick questions a staff member can ask about their own work.
const STAFF_SUGGESTED = [
  'My attendance this month',
  'My pending tasks',
  'Where do I upload thumbnails?',
  'How to paste claim links?',
];

const GRADIENT = 'linear-gradient(135deg,#10b981 0%,#0ea5e9 100%)';

function TypingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            width: 6, height: 6, borderRadius: '50%', background: '#10b981',
            display: 'inline-block', animation: `aiDot 1.2s ${i * 0.15}s infinite ease-in-out`
          }}
        />
      ))}
      <style>{'@keyframes aiDot{0%,80%,100%{opacity:.25;transform:translateY(0)}40%{opacity:1;transform:translateY(-3px)}}'}</style>
    </span>
  );
}

// Floating "Ask AI" chat in the bottom-right corner of every page: ask anything
// without leaving the page. Company revenue/expense stays admin-only — the server
// decides what this user's AI is allowed to see.
export default function AiWidget({ suggested = STAFF_SUGGESTED, title = 'Ask AI' }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [tips, setTips] = useState(null);
  const bottomRef = useRef(null);
  const voice = useVoiceInput(setText);
  const navigate = useNavigate();
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;

  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread, loading, open]);

  // Server decides which questions this user may be suggested (only their own access).
  useEffect(() => {
    if (!open || tips) return;
    api.get('/ai/status').then(r => setTips(r.data?.suggestions || [])).catch(() => setTips([]));
  }, [open, tips]);

  const ask = async (question) => {
    const q = (question || text).trim();
    if (!q || loading) return;
    const history = thread.map(m => ({ role: m.me ? 'user' : 'ai', text: m.text }));
    setText('');
    setThread(t => [...t, { me: true, text: q }]);
    setLoading(true);
    try {
      const res = await api.post('/ai/ask', { question: q, history });
      if (res.data?.file) downloadAiFile(res.data.file);
      setThread(t => [...t, { me: false, text: res.data?.answer || '', links: res.data?.links, rows: res.data?.rows, file: res.data?.file, plan: res.data?.plan, q }]);
    } catch (err) {
      setThread(t => [...t, { me: false, text: err.response?.data?.error || 'AI se jawab nahi mila' }]);
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <Tooltip title="AI se kuch bhi poochho" placement="left">
        <Button
          type="primary"
          size="large"
          shape="round"
          icon={<RobotOutlined style={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', right: 24, bottom: 148, zIndex: 1200, height: 46,
            paddingInline: 20, fontWeight: 600, letterSpacing: '.02em',
            boxShadow: '0 10px 26px rgba(16,185,129,0.45)',
            background: GRADIENT, border: 'none'
          }}
        >
          {title}
        </Button>
      </Tooltip>
    );
  }

  const chips = tips?.length ? tips : suggested;

  const shell = isMobile
    ? { position: 'fixed', inset: 0, width: 'auto', height: 'auto', borderRadius: 0 }
    : { position: 'fixed', right: 24, bottom: 24, width: 380, height: 560, borderRadius: 18 };

  return (
    <div
      style={{
        ...shell,
        zIndex: 1200, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: '#fff', boxShadow: '0 18px 48px rgba(20,30,70,0.28)',
        border: '1px solid rgba(16,185,129,0.18)'
      }}
    >
      <div style={{ background: GRADIENT, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar size={34} style={{ background: 'rgba(255,255,255,0.22)' }} icon={<RobotOutlined />} />
        <div style={{ flex: 1, lineHeight: 1.25 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{title}</div>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>
            {loading ? 'Soch raha hai...' : 'Online — apne kaam ke baare me poochho'}
          </div>
        </div>
        {thread.length > 0 && (
          <Tooltip title="Chat clear karo">
            <Button type="text" icon={<DeleteOutlined style={{ color: '#fff' }} />} onClick={() => setThread([])} />
          </Tooltip>
        )}
        <Button type="text" icon={<CloseOutlined style={{ color: '#fff' }} />} onClick={() => setOpen(false)} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, background: 'linear-gradient(180deg,#f6fbf9 0%,#f4f7fb 100%)' }}>
        {thread.length === 0 && !loading && (
          <div>
            <div style={{ textAlign: 'center', padding: '10px 0 16px' }}>
              <Avatar size={54} style={{ background: GRADIENT }} icon={<RobotOutlined />} />
              <div style={{ fontWeight: 700, marginTop: 10, fontSize: 15 }}>Namaste! Main aapki AI hoon</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Apne kaam, attendance, task ya dashboard ke baare me kuch bhi poochho
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <ThunderboltOutlined style={{ color: '#10b981' }} />
              <Text strong style={{ fontSize: 12 }}>Aap ye poochh sakte ho</Text>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {chips.map(s => (
                <Tag
                  key={s}
                  onClick={() => ask(s)}
                  style={{
                    cursor: 'pointer', margin: 0, padding: '6px 12px', borderRadius: 16,
                    background: '#fff', border: '1px solid #cfe9df', color: '#0f766e',
                    fontSize: 12, whiteSpace: 'normal'
                  }}
                >
                  {s}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {thread.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, justifyContent: m.me ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
            {!m.me && <Avatar size={28} style={{ background: GRADIENT, flexShrink: 0 }} icon={<RobotOutlined />} />}
            <div
              style={{
                maxWidth: '78%', fontSize: 13, lineHeight: 1.55, padding: '10px 13px',
                borderRadius: m.me ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                background: m.me ? GRADIENT : '#fff',
                color: m.me ? '#fff' : '#1f2430',
                border: m.me ? 'none' : '1px solid #e6e9f2',
                boxShadow: m.me ? '0 4px 14px rgba(14,165,233,0.25)' : '0 2px 10px rgba(60,70,120,0.07)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word'
              }}
            >
              {m.text.replace(/\*\*/g, '')}
              {!m.me && m.plan && <AiPlanCard plan={m.plan} />}
              {!m.me && Array.isArray(m.rows) && m.rows.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Button size="small" type="primary" ghost onClick={async () => {
                    if (m.file) { downloadAiFile(m.file); return; }
                    try {
                      const r = await api.post('/ai/rows-xlsx', { rows: m.rows, title: (m.q || 'AI List').slice(0, 60) });
                      downloadAiFile(r.data?.file);
                    } catch (e) { /* ignore */ }
                  }}>
                    {m.file?.name?.endsWith('.pdf') ? 'Download PDF' : 'Download Excel'} ({m.rows.length})
                  </Button>
                </div>
              )}
              {!m.me && Array.isArray(m.links) && m.links.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {m.links.map(l => (
                    <Button
                      key={l.path}
                      size="small"
                      onClick={() => { navigate(l.path); if (isMobile) setOpen(false); }}
                      style={{ borderRadius: 14, borderColor: '#10b981', color: '#0f766e', fontSize: 12 }}
                    >
                      Open {l.label} →
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {m.me && <Avatar size={28} style={{ background: '#64748b', flexShrink: 0 }} icon={<UserOutlined />} />}
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Avatar size={28} style={{ background: GRADIENT }} icon={<RobotOutlined />} />
            <div style={{ background: '#fff', border: '1px solid #e6e9f2', borderRadius: '14px 14px 14px 4px', padding: '12px 14px' }}>
              <TypingDots />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ padding: 12, borderTop: '1px solid #eef1f6', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            placeholder="Sawal likho ya mic dabakar bolo..."
            value={text}
            onChange={e => setText(e.target.value)}
            onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); ask(); } }}
            disabled={loading}
            style={{ borderRadius: 18, resize: 'none', paddingBlock: 8 }}
          />
          {voice.supported && (
            <Tooltip title={voice.listening ? 'Sun raha hoon...' : 'Bol ke poochho'}>
              <Button
                shape="circle"
                danger={voice.listening}
                icon={<AudioOutlined />}
                onClick={voice.toggle}
              />
            </Tooltip>
          )}
          <Button
            shape="circle"
            type="primary"
            icon={<SendOutlined />}
            onClick={() => ask()}
            loading={loading}
            style={{ background: GRADIENT, border: 'none' }}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 6, textAlign: 'center' }}>
          AI sirf wahi data dikhata hai jiska aapko access hai
        </Text>
      </div>
    </div>
  );
}
